import React, { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@this/constants/themes';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Carousel from 'react-material-ui-carousel';
import AppointContext from '@this/components/appoint/appoint_context';
import muiTheme from '@this/components/appoint/mui_theme';
import { H2, FilledTextField } from '@this/components/appoint/common';
import { SigninButtons } from '@this/components/appoint/users/sign_in_buttons';

interface Props {
  googleOauthPath: string;
  outlookOauthPath: string;
}

const SignInupForm = (props: Props) => {
  const contextValue = useContext(AppointContext);

  const isSignUp = () => {
    return location.pathname.indexOf('sign_up') > -1;
  };

  return (
    <Container maxWidth={false} style={{ padding: 0 }}>
      <TheBox display="flex" isMobile={contextValue.isMobile}>
        <TheLeft display="flex" isMobile={contextValue.isMobile}>
          <CarouselWrap isMobile={contextValue.isMobile}>
            <Carousel
              navButtonsAlwaysVisible
              interval={5000}
              NextIcon="次へ"
              PrevIcon="前へ"
              navButtonsWrapperProps={{
                style: {
                  top: 'auto',
                  bottom: 0,
                  zIndex: 10,
                  height: 30
                }
              }}
              navButtonsProps={{ className: 'nav_button' }}
              indicatorContainerProps={{
                style: {
                  position: 'absolute',
                  bottom: 0,
                  margin: '10px 0'
                }
              }}
              indicatorIconButtonProps={{
                style: {
                  color: muiTheme.palette.primary.main,
                  opacity: 0.4
                }
              }}
              activeIndicatorIconButtonProps={{
                style: {
                  opacity: 1
                }
              }}
            >
              <Slide style={{ paddingTop: contextValue.isMobile ? 0 : '77px' }}>
                <SlideImg
                  src="/images/aicalendar/signup_slide1.png"
                  width="538"
                  isMobile={contextValue.isMobile}
                />
                <SlideImgMobile src="/images/aicalendar/signup_slide_sp1.png" isMobile={contextValue.isMobile} />
                <SlideTextBox isMobile={contextValue.isMobile}>
                  <SlideTitle>AIカレンダーとは</SlideTitle>
                  <SlideText>
                    AIカレンダーはビジネスに特化した日程調整ツールです。
                    <br />
                    カンタンな条件設定から、予定の候補日をAIが提案してくれます。
                    <br />
                    あとはその候補日をメンバーにシェアするだけ！
                    <br />
                    日常の何気ない予定作成がもっとカンタンに。
                    <br />
                    あなたのビジネスをAIカレンダーでもっと快適に。
                  </SlideText>
                </SlideTextBox>
              </Slide>
              <Slide>
                <SlideImg
                  src="/images/aicalendar/signup_slide2.png"
                  width="760"
                  isMobile={contextValue.isMobile}
                />
                <SlideImgMobile src="/images/aicalendar/signup_slide_sp2.png" isMobile={contextValue.isMobile} />
                <SlideTextBox isMobile={contextValue.isMobile}>
                  <SlideTitle>
                    使い方はカンタン。
                    <br />
                    予定の条件を設定しておくだけ。
                  </SlideTitle>
                  <SlideText>
                    曜日会議の長さを設定するだけで、
                    <br />
                    スケジュールに合わせて日程候補をAIが提案。
                  </SlideText>
                </SlideTextBox>
              </Slide>
              <Slide>
                <SlideImg
                  src="/images/aicalendar/signup_slide3.png"
                  width="760"
                  isMobile={contextValue.isMobile}
                />
                <SlideImgMobile src="/images/aicalendar/signup_slide_sp3.png" isMobile={contextValue.isMobile} />
                <SlideTextBox isMobile={contextValue.isMobile}>
                  <SlideTitle>
                    候補日のシェアもカンタン。
                    <br />
                    日程調整のメッセージも自動で作成。
                  </SlideTitle>
                  <SlideText>
                    候補日が決まっても予定調整の依頼って意外に大変。
                    <br />
                    そんなときもAIカレンダーなら、依頼もカンタン。
                  </SlideText>
                </SlideTextBox>
              </Slide>
            </Carousel>
          </CarouselWrap>
        </TheLeft>
        <TheRight isMobile={contextValue.isMobile}>
          <Box
            style={{
              width: contextValue.isMobile ? 'auto' : '500px',
              maxWidth: '500px',
              marginRight: 'auto'
            }}
          >
            <TheTitle>
              <img
                src="/images/aicalendar/aicalendar_logo.png"
                height="26"
                style={{ marginRight: '5px', verticalAlign: 'text-bottom' }}
              />
              {`に${isSignUp() ? '新規登録' : 'ログイン'}しよう`}
            </TheTitle>
            <Description isMobile={contextValue.isMobile}>
              お持ちのアカウントと連携して今すぐはじめられます
            </Description>

            <SigninButtons googleOauthPath={props.googleOauthPath} outlookOauthPath={props.outlookOauthPath} />

            <Link
              component={RouterLink}
              to={`/users/sign_${isSignUp() ? 'in' : 'up'}`}
              underline="always"
              style={{
                display: 'block',
                margin: contextValue.isMobile ? '16px auto' : '30px 0 10px',
                width: 'fit-content'
              }}
            >
              <Login isMobile={contextValue.isMobile}>
                {isSignUp() ? 'すでにアカウントをお持ちの方はこちら' : '新規登録はこちら'}
              </Login>
            </Link>
          </Box>
        </TheRight>
      </TheBox>
    </Container>
  );
};

const TheBox = styled(Box)<{ isMobile: boolean }>`
  justify-content: center;
  min-height: calc(100vh - 54px);

  ${props =>
    props.isMobile &&
    `
    flex-direction: column-reverse;
  `}
`;

const TheLeft = styled(Box)<{ isMobile: boolean }>`
  flex-grow: 1;
  position: relative;
  z-index: 0;
  padding-right: 30px;

  ${props =>
    props.isMobile &&
    `
    padding-right: 0;
  `}
`;

const CarouselWrap = styled(Box)<{ isMobile: boolean }>`
  width: 600px;
  max-width: 600px;
  margin-left: auto;
  overflow: visible;

  div[class^='Carousel-root'] {
    overflow: visible;
    padding-bottom: 34px;
  }

  .nav_button {
    width: 80px;
    padding: 8px 12px;
    background: transparent;
    border: solid 1px #ddd;
    border-radius: 3px;
    text-align: center;
    display: block;
    color: #606060;
    font-size: 11px;
    line-height: 1;
    opacity: 1;

    &:hover {
      background: transparent;
      border-color: #606060;
      opacity: 1 !important;
    }
  }

  ${props =>
    props.isMobile &&
    `
    width: 100vw;
    max-width: 100vw;
    padding: 36px 0 60px;

    .nav_button {
      margin: 0 24px;
    }
  `}
`;

const Slide = styled(Box)`
  padding-bottom: 40px;
  text-align: center;
`;

const SlideImg = styled.img<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    display: none;
  `}
`;

const SlideImgMobile = styled.img<{ isMobile: boolean }>`
  max-width: calc(100vw - 60px);

  ${props =>
    !props.isMobile &&
    `
    display: none;
  `}
`;

const SlideTextBox = styled(Box)<{ isMobile: boolean }>`
  margin-top: 10px;

  ${props =>
    !props.isMobile &&
    `
    width: 340px;
    margin: 0 auto;
    text-align: left;
  `}
`;

const SlideTitle = styled.div`
  margin-bottom: 10px;
  color: ${muiTheme.palette.primary.main};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
`;

const SlideText = styled.div`
  font-size: 11px;
  line-height: 1.8;
`;

const TheRight = styled(Box)<{ isMobile: boolean }>`
  flex-grow: 1;
  position: relative;
  z-index: 10;
  padding: 200px 0 0 50px;
  background: #f7f7f7;

  ${props =>
    props.isMobile &&
    `
    padding: 50px 24px 36px;
    text-align: center;
  `}
`;

const TheTitle = styled(H2)`
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 500;
  line-height: 2;
`;

export const TheSmallTextField = styled(FilledTextField)`
  width: 30%;
  margin-right: 12px;
`;

const Description = styled(Typography)<{ isMobile: boolean }>`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${muiTheme.palette.primary.main};

  ${props =>
    props.isMobile &&
    `
    margin-bottom: 16px;
    font-size: 13px;
  `}
`;

const Login = styled.span<{ isMobile: boolean }>`
  display: block;
  position: relative;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;

  ${props =>
    props.isMobile &&
    `
    font-size: 12px;
  `}
`;

export default SignInupForm;
